import React, { useEffect, useState } from 'react';

import { Box } from '@material-ui/core';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { ChatUser, TeamMember } from '@vyce/core/src/types';
import { AppA } from '@vyce/core/src/components';

import { getTimesheetsTableColumns, RECENT_EMPLOYEES_TABLE_COLUMNS } from '../config';
import { GRID_PAGE_SIZE } from '../../../constants';
import useChatRequests from '../../../hooks/useChatRequests';
import { getAvatar } from '@vyce/core/src/utils/getAvatar';
import { formatTableDate } from '@vyce/core/src/utils/dates';
import { GridCellWithAvatar } from '../../../components/GridCellWithAvatar';

interface Props {
  data: any[];
  systemName: string;
  companyId: string;
  chatCreator?: ChatUser;
}

export const TableWidget = ({ data, systemName, companyId, chatCreator }: Props) => {
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [idFieldName, setIdFieldName] = useState<string>('id');
  const { createPrivateChat } = useChatRequests();

  const goToChat = async (timeMember: TeamMember) => {
    if (!chatCreator || !companyId) {
      return;
    }
    await createPrivateChat({
      creator: chatCreator,
      chatUser: {
        uid: timeMember.user_id,
        name: `${timeMember.first_name} ${timeMember.last_name}`,
        email: timeMember.email,
        phone: timeMember.phone,
        avatarUrl: timeMember.avatar,
      },
      companyId,
    });
  };

  const birthdaysTableColumns: GridColDef[] = [
    {
      field: 'first_name',
      headerName: 'Name',
      flex: 0.2,
      minWidth: 140,
      disableColumnMenu: true,
      renderCell: params => (
        <GridCellWithAvatar
          avatarUrl={params.row.avatar}
          avatarPlaceholder={getAvatar(params.row.gender)}
          link={`/teams/dashboard/${params.row?.first_name}_${params.row?.user_id}`}
          name={`${params.row.first_name} ${params.row.last_name}`}
          target="_blank"
          createPrivateChat={() => goToChat(params.row as TeamMember)}
        />
      ),
    },
    {
      field: 'current_age',
      headerName: 'Age',
      flex: 0.1,
      minWidth: 100,
      disableColumnMenu: true,
    },
    {
      field: 'date_of_birth',
      headerName: 'Date of Birth',
      flex: 0.15,
      minWidth: 120,
      valueGetter: params => formatTableDate(params.row?.date_of_birth),
      disableColumnMenu: true,
    },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 270,
      renderCell: params => <AppA content={params.row.email} href={`mailto: ${params.row.email}`} />,
      disableColumnMenu: true,
    },
    {
      field: 'phone',
      headerName: 'Phone Number',
      minWidth: 180,
      disableColumnMenu: true,
      renderCell: params => <AppA content={params.row.phone} href={`tel: ${params.row.phone}`} />,
    },
  ];

  useEffect(() => {
    let cols: GridColDef[] = [];
    switch (systemName) {
      case 'team_members_birthdays':
        cols = birthdaysTableColumns;
        setIdFieldName('user_id');
        break;
      case 'recent_timesheets':
        cols = getTimesheetsTableColumns(companyId);
        setIdFieldName('pay_run_id');
        break;
      case 'recent_registered_employees':
        cols = RECENT_EMPLOYEES_TABLE_COLUMNS;
        setIdFieldName('user_id');
        break;
    }
    setColumns(cols);
  }, [systemName]);

  return (
    <Box width="100%" height="100%">
      <DataGrid
        rows={data}
        getRowId={data => data[idFieldName]}
        columns={columns}
        rowCount={data.length}
        pageSize={GRID_PAGE_SIZE}
        rowsPerPageOptions={[data.length]}
        disableSelectionOnClick
      />
    </Box>
  );
};
